import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import {
  Heart,
  Globe,
  DollarSign,
  Shield,
  ArrowRight,
  Users,
  Building2,
  CheckCircle,
} from 'lucide-react';

export default function About() {
  const { t } = useTranslation();

  const values = t('public.about.values', { returnObjects: true }) as Array<{
    title: string;
    description: string;
  }>;

  const valueIcons = [
    Heart,
    Globe,
    DollarSign,
    Shield,
  ];

  const valueColors = [
    'bg-red-500/10 text-red-500',
    'bg-blue-500/10 text-blue-500',
    'bg-green-500/10 text-green-500',
    'bg-purple-500/10 text-purple-500',
  ];

  const stats = [
    { value: '10K+', label: 'Organizations' },
    { value: '500K+', label: 'Members Managed' },
    { value: '6', label: 'Languages' },
    { value: '11', label: 'Currencies' },
  ];

  return (
    <div className="flex flex-col">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-b from-primary/10 via-background to-background py-16 sm:py-20 lg:py-24">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground mb-4">
              {t('public.about.title')}
            </h1>
            <p className="text-lg text-muted-foreground">
              Empowering communities worldwide with simple, powerful organization management tools
            </p>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-16 sm:py-20 bg-background">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
              <div>
                <div className="inline-flex items-center space-x-2 bg-primary/10 text-primary rounded-full px-4 py-1.5 text-sm font-medium mb-6">
                  <Heart className="h-4 w-4" />
                  <span>{t('public.about.mission.title')}</span>
                </div>
                <h2 className="text-2xl sm:text-3xl font-bold mb-4">
                  {t('public.about.mission.title')}
                </h2>
                <p className="text-muted-foreground text-base sm:text-lg leading-relaxed">
                  {t('public.about.mission.description')}
                </p>
              </div>
              <div className="relative">
                <div className="bg-gradient-to-br from-primary/20 to-primary/5 rounded-2xl p-6 sm:p-8">
                  <div className="grid grid-cols-2 gap-4">
                    {stats.map((stat, index) => (
                      <div key={index} className="bg-background rounded-xl p-4 text-center shadow-sm">
                        <p className="text-2xl sm:text-3xl font-bold text-primary">{stat.value}</p>
                        <p className="text-xs sm:text-sm text-muted-foreground">{stat.label}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-16 sm:py-20 bg-muted/30">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl font-bold mb-4">
              Our Values
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              The principles that guide everything we do at Tickally
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => {
              const Icon = valueIcons[index];
              const colorClass = valueColors[index];

              return (
                <Card key={index} className="border-border/50 hover:border-primary/50 transition-colors">
                  <CardContent className="p-6 text-center">
                    <div className={`w-12 h-12 ${colorClass} rounded-xl flex items-center justify-center mx-auto mb-4`}>
                      <Icon className="h-6 w-6" />
                    </div>
                    <h3 className="font-semibold text-lg mb-2">{value.title}</h3>
                    <p className="text-sm text-muted-foreground">
                      {value.description}
                    </p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Who We Serve Section */}
      <section className="py-16 sm:py-20 bg-background">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl font-bold mb-4">
              Who We Serve
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Tickally is designed for a wide range of informal organizations
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            <Card className="border-border/50">
              <CardContent className="p-6">
                <div className="w-10 h-10 bg-blue-500/10 rounded-lg flex items-center justify-center mb-4">
                  <Building2 className="h-5 w-5 text-blue-500" />
                </div>
                <h3 className="font-semibold mb-2">Cultural Associations</h3>
                <p className="text-sm text-muted-foreground">
                  Preserve and promote cultural heritage while managing membership and collecting dues.
                </p>
              </CardContent>
            </Card>

            <Card className="border-border/50">
              <CardContent className="p-6">
                <div className="w-10 h-10 bg-purple-500/10 rounded-lg flex items-center justify-center mb-4">
                  <svg className="h-5 w-5 text-purple-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                </div>
                <h3 className="font-semibold mb-2">Religious Organizations</h3>
                <p className="text-sm text-muted-foreground">
                  Churches, mosques, temples, and other faith-based groups tracking attendance and offerings.
                </p>
              </CardContent>
            </Card>

            <Card className="border-border/50">
              <CardContent className="p-6">
                <div className="w-10 h-10 bg-green-500/10 rounded-lg flex items-center justify-center mb-4">
                  <Users className="h-5 w-5 text-green-500" />
                </div>
                <h3 className="font-semibold mb-2">Alumni Groups</h3>
                <p className="text-sm text-muted-foreground">
                  School and university alumni associations managing reunions and fundraising events.
                </p>
              </CardContent>
            </Card>

            <Card className="border-border/50">
              <CardContent className="p-6">
                <div className="w-10 h-10 bg-orange-500/10 rounded-lg flex items-center justify-center mb-4">
                  <svg className="h-5 w-5 text-orange-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </div>
                <h3 className="font-semibold mb-2">Cooperatives</h3>
                <p className="text-sm text-muted-foreground">
 Member-owned businesses and savings groups managing contributions and distributions.
                </p>
              </CardContent>
            </Card>

            <Card className="border-border/50">
              <CardContent className="p-6">
                <div className="w-10 h-10 bg-pink-500/10 rounded-lg flex items-center justify-center mb-4">
                  <svg className="h-5 w-5 text-pink-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                  </svg>
                </div>
                <h3 className="font-semibold mb-2">Non-Profits</h3>
                <p className="text-sm text-muted-foreground">
                  Charitable organizations tracking donors, managing events, and generating reports.
                </p>
              </CardContent>
            </Card>

            <Card className="border-border/50">
              <CardContent className="p-6">
                <div className="w-10 h-10 bg-cyan-500/10 rounded-lg flex items-center justify-center mb-4">
                  <CheckCircle className="h-5 w-5 text-cyan-500" />
                </div>
                <h3 className="font-semibold mb-2">And More</h3>
                <p className="text-sm text-muted-foreground">
                  Any group that needs to manage members, track attendance, and collect payments.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Global Reach Section */}
      <section className="py-16 sm:py-20 bg-muted/30">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <Globe className="h-12 w-12 text-primary mx-auto mb-6" />
            <h2 className="text-2xl sm:text-3xl font-bold mb-4">
              Global Reach, Local Support
            </h2>
            <p className="text-muted-foreground text-base sm:text-lg mb-8">
              Tickally supports 6 languages and 11 currencies, making it accessible to organizations 
              around the world. From Nigeria to India, from France to Brazil, communities are using 
              Tickally to streamline their operations.
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              {['English', 'Français', 'Español', 'Italiano', 'Deutsch', '中文'].map((lang) => (
                <span
                  key={lang}
                  className="px-4 py-2 bg-background rounded-full text-sm font-medium border"
                >
                  {lang}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 sm:py-20 bg-primary">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-primary-foreground mb-4">
            Join the Tickally Community
          </h2>
          <p className="text-primary-foreground/80 mb-8 max-w-xl mx-auto">
            Start managing your organization more effectively today. It&apos;s free to get started.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/org-signup">
              <Button size="lg" variant="secondary" className="w-full sm:w-auto">
                Register Your Organization
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
            <Link to="/how-it-works">
              <Button size="lg" variant="outline" className="w-full sm:w-auto border-primary-foreground text-primary-foreground hover:bg-primary-foreground/10">
                Learn More
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
